import { ExhibitorsFilter } from './filters/exhibitors-filter';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { uniqBy, isArray, last, first, filter } from 'lodash-es';
import { Subject, take, takeUntil } from 'rxjs';
import { Location } from '@angular/common';
import { kebabCase } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'sbc-exhibitors-directory',
  templateUrl: './exhibitors-directory.component.html',
  styleUrls: ['./exhibitors-directory.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class ExhibitorsDirectoryComponent implements OnInit, OnDestroy {
  @ViewChild('widget', { read: ElementRef, static: false }) widget!: ElementRef<HTMLElement>;
  @Input() customstyles!: string;
  @Input() eventpath!: string;
  @Input() subeventid!: string;
  @Input() primarycolor!: string;
  @Input() showcount!: number;
  @Input() alphabet: 'on' | 'off' = 'on';
  @Input() lang: string = 'en';

  destroy$: Subject<void> = new Subject<void>();
  isLoadingDirectoryData = true;

  eventColors!: EventColors;
  categories!: CompanyCategory[];
  zones!: Zone[];
  exhibitors!: ExhibitorEntry[];

  private _selectedEntry: ExhibitorEntry | null = null;
  relatedExhibitors: ExhibitorEntry[] = [];

  set selectedEntry(entry: ExhibitorEntry | null) {
    this._selectedEntry = entry;
    this.relatedExhibitors = this.findRelatedEntries(entry);
  };

  get selectedEntry(): ExhibitorEntry | null {
    return this._selectedEntry;
  }

  shownExhibitorsCount = 10;
  hasMoreEntriesThanVisible = false;
  searchPhrase: string = '';
  selectedCategoryId: string | null = null;
  selectedZones: string[] = [];
  selectedCharacter: string = '';

  QueryParams: QueryParams = {
    Page: 'page',
    Search: 'search',
    Character: 'character',
    Category: 'category',
    Exhibitor: 'exhibitor',
    Zone: 'zone',
  };

  initialPathName: string = window.location.pathname;
  exhibitorSelectStack: string[] = [];
  queriedExhibitors!: ExhibitorEntry[];

  pageFilter = new ExhibitorsFilter(this.QueryParams.Page, (exhs) => exhs.slice(0, this.shownExhibitorsCount));
  searchFilter = new ExhibitorsFilter(this.QueryParams.Search, (exhs) => exhs.filter(exh => exh.name.toLowerCase().includes(this.searchPhrase.toLowerCase())));
  characterFilter = new ExhibitorsFilter(this.QueryParams.Character, (exhs) => {
    if (this.alphabet === 'off' || !this.selectedCharacter) return exhs;
    return exhs.filter(exh => exh.name[0].toUpperCase() === this.selectedCharacter);
  });
  categoryFilter = new ExhibitorsFilter(this.QueryParams.Category, (exhs) => {
    if (!this.selectedCategoryId) return exhs;
    return exhs.filter(exh => exh.categoryId === this.selectedCategoryId)
  });
  zoneFilter = new ExhibitorsFilter(this.QueryParams.Zone, (exhs) => {
    if (!this.selectedZones.length) return exhs;
    return exhs.filter(exh => exh.zoneId && this.selectedZones.includes(exh.zoneId))
  });

  constructor(
    private _http: HttpClient,
    private _cdr: ChangeDetectorRef,
    private _location: Location,
    private _translate: TranslateService,
  ) {
    this.characterFilter.filteredExhibitorsCount$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(count => this.hasMoreEntriesThanVisible = count > this.shownExhibitorsCount);

    this.characterFilter.filteredExhibitors$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((queriedExhibitors) => this.queriedExhibitors = queriedExhibitors);
  }

  initTranslate() {
    this._translate.addLangs(['en', 'it', 'es']);
    this._translate.setDefaultLang('en');
    this._translate.use(this.lang);
  }

  ngOnInit() {
    this.initTranslate();
    if (this.customstyles) {
      this.injectStyle(this.customstyles);
    }
    if (this.showcount) {
      this.shownExhibitorsCount = +this.showcount;
    }
    this._http.get(`${environment.baseUrl}api/event/${this.eventpath}/exhibitors-directory`).subscribe(r => {
      this.mapExhibitorsDirectoryData(r as any);
      this.isLoadingDirectoryData = false;
      this._cdr.detectChanges();
      this.initState();
      this._cdr.detectChanges();
    });
  }

  mapExhibitorsDirectoryData({ data }: { data: { event: any[], taxonomy: any[] } }) {
    const allExisitngCompanyCategories: CompanyCategory[] = data.taxonomy[0]?.taxonomy.contentItems.map((tax: {
      displayText: string,
      contentItemId: string
    }) => ({
      categoryName: tax.displayText,
      categoryId: tax.contentItemId
    }));

    this.eventColors = data.event[0]?.colors as EventColors;

    const exhibitorsContainers = data.event[0]?.exhibitorsContainer.contentItems;
    let preMappedExhibitors = this.subeventid ?
      exhibitorsContainers
        .filter((ec: { contentItemId: string; }) => ec.contentItemId === this.subeventid)[0]?.list.contentItems
      :
      exhibitorsContainers
        .map((ec: any) => {
          const zoneId = ec.contentItemId;
          return ec.list.contentItems.map((ex: any) => ({
            ...ex,
            zoneId,
          }));
        })
        .flat();

    const hasMultipleContainers = exhibitorsContainers.length > 1;

    this.exhibitors = preMappedExhibitors.filter((ex: any) => ex.company.contentItems[0]).map((ex: any) => {
      const materialContainers = ex.list ? ex.list.contentItems.flat() : [];
      const allMaterials: MaterialItem[] = materialContainers.length > 1 ?
      materialContainers.flatMap((mc: any) => mc.bag.contentItems)
      : materialContainers[0]?.bag.contentItems;
      const company = ex.company.contentItems[0];
      const companyTranslatedDescription = company.translations?.contentItems
        .find((t: { language: { language: string; }; }) => t.language?.language == this.lang)?.description;
      const logoUrl = company.companyLogo?.urls[0];
      const exhibitorEntry: ExhibitorEntry = {
        ...company,
        description: companyTranslatedDescription ? companyTranslatedDescription : company.description,
        calendlyLink: ex.calendly,
        standNumber: ex.standNumber,
        exhibitorId: ex.contentItemId,
        excludeFromExhibitorsWidget: ex.excludeFromExhibitorsWidget,
        companyId: company.contentItemId,
        websiteDomain: this.getWebsiteDomain(company.website),
        logoUrl: logoUrl ? `${environment.blobStorageUrl}${logoUrl}` : company.photoUrl,
        categoryId: company.companyCategory?.termContentItems ? company.companyCategory?.termContentItems[0]?.contentItemId : null,
        categoryName: company.companyCategory?.termContentItems ? company.companyCategory?.termContentItems[0]?.displayText : null,
        zoneId: ex.zoneId,
        presentations: allMaterials?.filter(m => m.materialType == 'doc' && m.media.urls.some(url => url.endsWith('.pdf'))).map(m => ({
          ...m,
          src: m.media.urls[0],
          thumbnailSrc: m.media.urls[0].replace('/Presentations/', '/thumbnails/').replace('.pdf', '-preview.png'),
        })).slice(0, 2),
        video: allMaterials?.find(m => m.materialType === 'video'),
      };
      return exhibitorEntry;
    });

    this.exhibitors = this.exhibitors.filter(e => !e.excludeFromExhibitorsWidget);

    if (hasMultipleContainers) {
      this.exhibitors = uniqBy(this.exhibitors, 'companyId');
    }
    if (hasMultipleContainers && !this.subeventid) {
      this.zones = exhibitorsContainers.map((ec: any) => ({
        zoneName: ec.zoneName ?? ec.displayText,
        zoneId: ec.contentItemId
      }));
    }

    this.categories = allExisitngCompanyCategories.filter(aec => this.exhibitors.some(ex => ex.categoryId === aec.categoryId));
  }

  trackByFn(index: number, exhibitor: ExhibitorEntry) {
    return exhibitor.companyId;
  }

  getWebsiteDomain(website: string): string {
    if (!website) return '';
    let domain = website;
    try {
      const url = new URL(website);
      domain = url.host;
    } catch (err) {
      console.error(`${website} is not a valid url`);
    }
    return domain.replace('www.', '');
  }

  injectStyle(styleContent: string) {
    const shadowRoot = document.querySelector('sbc-exhibitors-directory')?.shadowRoot;
    const styles = document.createElement('style');
    styles.innerHTML = styleContent;
    shadowRoot?.appendChild(styles);
  }

  incrementShownExhibitorsCount() {
    this.shownExhibitorsCount += 10;
  }

  findRelatedEntries(entry: ExhibitorEntry | null): ExhibitorEntry[] {
    if (!entry || !entry.categoryId) return [];
    return this.exhibitors.filter(exh => exh.companyId !== entry.companyId && exh.categoryId === entry.categoryId).splice(0, 3);
  }

  replaceLocation(name: string, value: string): void {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(name, value);
    } else {
      url.searchParams.delete(name);
    }
    this._location.replaceState(url.toString().replace(window.location.origin, ''));
  }

  selectCategory(categoryId: string) {
    this.selectedCategoryId = categoryId;
    this.onCategorySelect(categoryId);
    this.widget.nativeElement.scrollIntoView();
  }

  onCategorySelect(categoryId: string) {
    const category = this.categories.find(cat => cat.categoryId === categoryId);
    this.onFilterApplied(this.QueryParams.Category, category?.categoryName!);
  }

  onZoneSelect(zones: Zone[]) {
    if (!isArray(zones)) { return; }
    const zonesParam = zones.length ? [...zones].map(z => z.zoneName).join(',,') : '';
    this.onFilterApplied(this.QueryParams.Zone, zonesParam);
  }

  onFilterApplied(name: string, value: string) {
    this.clearSelectedExhibitor();
    this.replaceLocation(name, value);
  }

  onExhibitorSelected(exhibitor: ExhibitorEntry) {
    this.exhibitorSelectStack.push(window.location.search);
    this.selectExhibitor(exhibitor);
  }

  selectExhibitor(exhibitor: ExhibitorEntry) {
    this.selectedEntry = exhibitor;
    this.clearFilters();
    this.replaceLocation(this.QueryParams.Exhibitor, kebabCase(exhibitor.name));
    this.widget.nativeElement.scrollIntoView();
  }

  clearSelectedExhibitor() {
    const url = new URL(window.location.href);
    url.searchParams.delete(this.QueryParams.Exhibitor)
    this._location.replaceState(url.toString().replace(window.location.origin, ''));
    this.selectedEntry = null;
  }

  initState() {
    const url = new URL(window.location.href);
    if (url.searchParams.has(this.QueryParams.Exhibitor)) {
      this.selectedEntry = this.exhibitors.find(exh => kebabCase(exh.name) === kebabCase(url.searchParams.get(this.QueryParams.Exhibitor)!)) ?? null;
      return;
    }
    this.searchPhrase = url.searchParams.get(this.QueryParams.Search) ?? '';
    const categoryName = url.searchParams.get(this.QueryParams.Category);
    const zones = url.searchParams.get(this.QueryParams.Zone);
    if (categoryName) {
      this.selectedCategoryId = this.categories.find(cat => cat.categoryName == categoryName)?.categoryId ?? null;
    }
    if (zones) {
      this.selectedZones = zones.split(',,').map(zoneId => this.zones.find(z => z.zoneName === zoneId)?.zoneId ?? '').filter(z => !!z);
    }
    this.selectedCharacter = url.searchParams.get(this.QueryParams.Character) ?? '';
  };

  clearFilters() {
    const url = new URL(window.location.href);
    Object.keys(ExhibitorsFilter.intances).forEach(filterName => url.searchParams.delete(filterName));
    this._location.replaceState(url.toString().replace(window.location.origin, ''));

    this.searchPhrase = '';
    this.selectedCategoryId = null;
    this.selectedCharacter = '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack() {
    const lastQuery = last(this.exhibitorSelectStack) || this.initialPathName;
    this._location.replaceState(lastQuery as string);
    this.exhibitorSelectStack.pop();
    this.initState();
    const url = new URL(window.location.href);
    if (url.searchParams.get(this.QueryParams.Exhibitor)) {
      return;
    }
    this.selectedEntry = null;
  }
  @HostListener('document:keydown', ['$event'])
  handleArrowKeys(event: KeyboardEvent) {
    if (!this.selectedEntry) { return; }
    if (event.key == 'ArrowLeft') {
      this.displayPrevious();
    }
    if (event.key == 'ArrowRight') {
      this.displayNext();
    }
  }

  displayPrevious() {
    const previousEntry = this.queriedExhibitors[this.getCurrentExhibitorIndexInQueriedList() - 1] || last(this.queriedExhibitors);
    this.selectExhibitor(previousEntry);
  }
  displayNext() {
    const nextEntry = this.queriedExhibitors[this.getCurrentExhibitorIndexInQueriedList() + 1] || first(this.queriedExhibitors);
    this.selectExhibitor(nextEntry);
  }
  getCurrentExhibitorIndexInQueriedList() {
    return this.queriedExhibitors.findIndex(qe => qe.companyId == this.selectedEntry?.companyId);
  }
}
